import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const PrivacyReacceptGate = () => {
  const { user, loading: authLoading } = useAuth();
  const [needsAccept, setNeedsAccept] = useState(false);
  const [latestId, setLatestId] = useState<string | null>(null);
  const [latestUpdated, setLatestUpdated] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading || !user) {
      setNeedsAccept(false);
      return;
    }
    (async () => {
      const { data: latest } = await supabase
        .from("privacy_policy" as any)
        .select("id,last_updated,updated_at")
        .eq("is_published", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!latest || !(latest as any).id) return;
      const latestRow = latest as any;

      const { data: acceptance } = await supabase
        .from("privacy_acceptances" as any)
        .select("privacy_id")
        .eq("user_id", user.id)
        .eq("privacy_id", latestRow.id)
        .limit(1)
        .maybeSingle();

      if (!acceptance) {
        setLatestId(latestRow.id);
        setLatestUpdated(latestRow.last_updated);
        setAgreed(false);
        setNeedsAccept(true);
      }
    })();
  }, [user, authLoading]);

  const handleAccept = async () => {
    if (!user || !latestId) return;
    setSubmitting(true);
    const { error } = await supabase.from("privacy_acceptances" as any).insert({
      user_id: user.id,
      privacy_id: latestId,
      accepted_at: new Date().toISOString(),
    });
    setSubmitting(false);
    if (error) {
      toast.error("Failed to record acceptance: " + error.message);
      return;
    }
    toast.success("Thanks for accepting the updated Privacy Policy.");
    setNeedsAccept(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setNeedsAccept(false);
  };

  return (
    <Dialog open={needsAccept}>
      <DialogContent
        className="max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Updated Privacy Policy</DialogTitle>
          <DialogDescription>
            Our Privacy Policy has been updated
            {latestUpdated &&
              ` on ${new Date(latestUpdated).toLocaleDateString()}`}
            . Please review and accept the new Privacy Policy to continue using
            BestPriceRide.com.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <Link
            to="/privacy"
            target="_blank"
            className="text-primary hover:underline text-sm font-medium"
          >
            Read the updated Privacy Policy →
          </Link>
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <Checkbox
            id="reaccept-privacy"
            checked={agreed}
            onCheckedChange={(v) => setAgreed(v === true)}
            className="mt-1"
          />
          <span className="text-sm text-foreground leading-relaxed">
            I have read and agree to the updated Privacy Policy.
          </span>
        </label>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleSignOut} disabled={submitting}>
            Sign out
          </Button>
          <Button onClick={handleAccept} disabled={!agreed || submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Accept & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyReacceptGate;
